import { useEffect, useState } from "react";
import {
  Fire, Warning, Trophy, GraduationCap, Star, Confetti, CalendarCheck,
  Megaphone, Medal, Lightning, ArrowUpRight,
} from "@phosphor-icons/react";
import { getNewsFeed } from "../lib/api";

const TPL = {
  breaking:    { label: "BREAKING", Icon: Fire,          grad: "from-rose-500 to-red-600",      ring: "ring-rose-400", urgent: true },
  alert:       { label: "ALERT",    Icon: Warning,       grad: "from-amber-400 to-orange-500",  ring: "ring-amber-400", urgent: true },
  result:      { label: "RESULT",   Icon: Trophy,        grad: "from-emerald-400 to-teal-600",  ring: "ring-emerald-400" },
  admission:   { label: "ADMISSION",Icon: GraduationCap, grad: "from-sky-400 to-blue-600",      ring: "ring-sky-400" },
  ranking:     { label: "RANKING",  Icon: Star,          grad: "from-violet-500 to-purple-700", ring: "ring-violet-400" },
  event:       { label: "EVENT",    Icon: Confetti,      grad: "from-pink-500 to-fuchsia-600",  ring: "ring-pink-400" },
  schedule:    { label: "SCHEDULE", Icon: CalendarCheck, grad: "from-indigo-400 to-indigo-700", ring: "ring-indigo-400" },
  notice:      { label: "NOTICE",   Icon: Megaphone,     grad: "from-teal-400 to-cyan-600",     ring: "ring-teal-400" },
  achievement: { label: "AWARD",    Icon: Medal,         grad: "from-orange-400 to-amber-600",  ring: "ring-orange-400" },
};
const tpl = (t) => TPL[t] || TPL.notice;

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const isToday = (iso) => iso && (Date.now() - new Date(iso).getTime()) < 86400000;

export function CollegeNews({ code, name, variant = "rail" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!code) return;
    getNewsFeed({ college_code: code, window: "month", limit: 30 })
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [code]);

  if (!items || items.length === 0) return null;

  // strict chronological (newest first); crawled items share a timestamp so keep feed order stable
  const sorted = [...items].sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
  const todayCount = sorted.filter((i) => i.pub_confirmed && isToday(i.published_at)).length;
  const marquee = sorted.slice(0, 10).map((i) => i.title).join("      \u2726      ");

  // ─── Sidebar variant: vertically stacked compact cards, no marquee ───────
  if (variant === "sidebar") {
    return (
      <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white" data-testid="college-news-sidebar">
        <div className="px-3 py-2.5 bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 text-white flex items-center gap-2 overflow-hidden relative">
          <Lightning size={14} weight="fill" className="shrink-0" style={{ animation: "cnBolt 1.6s ease-in-out infinite" }} />
          <span className="text-xs font-bold uppercase tracking-[0.2em]">Latest Updates</span>
          {todayCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-black/30 text-[9px] font-black uppercase tracking-wider">
              <span className="w-1 h-1 rounded-full bg-white" style={{ animation: "cnPulse 1s ease-in-out infinite" }} /> {todayCount}
            </span>
          )}
        </div>
        <div className="max-h-[560px] overflow-y-auto divide-y divide-zinc-100">
          {sorted.slice(0, 20).map((it, i) => {
            const T = tpl(it.template);
            const today = it.pub_confirmed && isToday(it.published_at);
            return (
              <a key={it.id || i}
                href={it.url || undefined} target={it.url ? "_blank" : undefined} rel="noreferrer"
                data-testid={`sidebar-news-item-${i}`}
                className={`group flex gap-2 p-3 hover:bg-zinc-50 transition-colors ${it.url ? "cursor-pointer" : "cursor-default"}`}
                style={{ animation: `cnRise .35s ease ${Math.min(i, 8) * 0.03}s both` }}>
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br ${T.grad} text-white shrink-0 shadow-sm`}>
                  <T.Icon size={13} weight="fill" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-[8px] font-black uppercase tracking-widest bg-gradient-to-r ${T.grad} bg-clip-text text-transparent`}>{T.label}</span>
                    {today && <span className="text-[8px] font-black uppercase text-rose-600">· NEW</span>}
                    <span className="ml-auto font-mono text-[9px] font-bold text-emerald-600 shrink-0">{ago(it.published_at)}</span>
                  </div>
                  <p className="text-[12px] font-semibold text-[#09090B] leading-snug line-clamp-2 group-hover:text-[#6043EF] transition-colors">{it.title}</p>
                  {it.url && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-600 mt-1 group-hover:gap-1 transition-all">
                      Source <ArrowUpRight size={9} weight="bold" />
                    </span>
                  )}
                </div>
              </a>
            );
          })}
        </div>
        <style>{`
          @keyframes cnBolt{0%,100%{transform:scale(1) rotate(0)}45%{transform:scale(1.2) rotate(-8deg)}55%{transform:scale(1.2) rotate(8deg)}}
          @keyframes cnPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
          @keyframes cnRise{0%{transform:translateY(8px);opacity:0}100%{transform:translateY(0);opacity:1}}
        `}</style>
      </div>
    );
  }

  // ─── Default rail variant (full-width horizontal) ────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-6" data-testid="college-news">
      <div className="relative rounded-2xl overflow-hidden border-2 border-rose-200 shadow-[0_10px_40px_-12px_rgba(244,63,94,.35)]"
        style={{ background: "linear-gradient(120deg,#fff7f5,#fff 40%,#f5faff)" }}>
        {/* animated color wash */}
        <div aria-hidden className="absolute inset-0 opacity-60 pointer-events-none">
          <div className="absolute -top-16 -left-10 w-64 h-64 rounded-full bg-rose-300/30 blur-3xl" style={{ animation: "cnBlob 9s ease-in-out infinite" }} />
          <div className="absolute -bottom-16 right-10 w-64 h-64 rounded-full bg-sky-300/30 blur-3xl" style={{ animation: "cnBlob 12s ease-in-out infinite reverse" }} />
        </div>

        {/* header */}
        <div className="relative flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 text-white overflow-hidden">
          <span aria-hidden className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-20deg] bg-white/25" style={{ animation: "cnSweep 5s linear infinite" }} />
          <Lightning size={18} weight="fill" className="shrink-0" style={{ animation: "cnBolt 1.6s ease-in-out infinite" }} />
          <span className="font-black tracking-tight text-sm sm:text-base uppercase shrink-0">Latest Updates &amp; Events</span>
          {todayCount > 0 && (
            <span className="shrink-0 inline-flex items-center gap-1 px-2 h-5 rounded-full bg-black/30 text-[10px] font-black uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-white" style={{ animation: "cnPulse 1s ease-in-out infinite" }} /> {todayCount} today
            </span>
          )}
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest opacity-90 hidden sm:inline">{items.length} bulletins · {name}</span>
        </div>

        {/* chronological horizontal timeline */}
        <div className="relative px-4 py-4">
          <div className="relative flex gap-3 overflow-x-auto pt-2 pb-2 cn-scroll" data-testid="college-news-track">
            {sorted.map((it, i) => {
              const T = tpl(it.template);
              const today = it.pub_confirmed && isToday(it.published_at);
              const hasBlob = !!(it.body && it.body.trim());
              return (
                <a key={it.id || i} data-testid={`college-news-card-${i}`}
                  href={it.url || undefined} target={it.url ? "_blank" : undefined} rel="noreferrer"
                  className={`group relative shrink-0 w-[240px] flex flex-col rounded-xl bg-white border border-zinc-200 shadow-sm overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-200 ${it.url ? "cursor-pointer" : "cursor-default"} ${T.urgent ? `ring-2 ${T.ring} ring-offset-1` : ""}`}
                  style={{ animation: `cnRise .5s ease ${Math.min(i, 10) * 0.05}s both` }}>
                  {/* top timeline marker strip */}
                  <span className={`block h-1.5 bg-gradient-to-r ${T.grad}`} />
                  {/* urgent corner ribbon */}
                  {T.urgent && (
                    <span className="absolute top-3 right-2 z-10 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[8px] font-black uppercase tracking-widest shadow"
                      style={{ animation: "cnPulse 1.1s ease-in-out infinite" }}>Urgent</span>
                  )}
                  <div className="flex flex-col flex-1 p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br ${T.grad} text-white shadow`}>
                        <T.Icon size={13} weight="fill" />
                      </span>
                      <span className={`text-[8px] font-black uppercase tracking-widest bg-gradient-to-r ${T.grad} bg-clip-text text-transparent`}>{T.label}</span>
                      {today && <span className="ml-auto inline-flex items-center gap-0.5 text-[8px] font-black uppercase text-rose-600"><span className="w-1 h-1 rounded-full bg-rose-600" style={{ animation: "cnPulse 1s ease-in-out infinite" }} />New</span>}
                    </div>
                    {/* title — gets more room when there's no blob */}
                    <p className={`text-[12px] font-bold text-[#09090B] leading-snug ${hasBlob ? "line-clamp-2" : "line-clamp-4"}`}>{it.title}</p>
                    {/* blob only when we actually have a subtitle/body */}
                    {hasBlob && <p className="text-[10px] text-zinc-500 leading-snug line-clamp-3 mt-1">{it.body}</p>}
                    <div className="mt-auto pt-2 flex items-center justify-between border-t border-zinc-100">
                      <span className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] font-bold text-emerald-600">{ago(it.published_at)}</span>
                        {it.pub_confirmed && (
                          <span className="text-[9px] text-zinc-400">{new Date(it.published_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                        )}
                      </span>
                      {it.url && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-600 group-hover:gap-1 transition-all">
                          Source <ArrowUpRight size={10} weight="bold" />
                        </span>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
            <div className="shrink-0 w-2" />
          </div>
        </div>

        {/* bottom flash marquee */}
        <div className="relative flex items-center h-7 bg-zinc-900 overflow-hidden">
          <span className="z-10 shrink-0 h-full px-2.5 flex items-center gap-1 bg-rose-600 text-white text-[9px] font-black uppercase tracking-[0.2em]">
            <Fire size={11} weight="fill" /> Flash
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="whitespace-nowrap will-change-transform" style={{ animation: "cnTicker 28s linear infinite" }}>
              <span className="text-[11px] font-bold text-amber-300 px-4">{marquee}</span>
              <span className="text-[11px] font-bold text-amber-300 px-4">{marquee}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cn-scroll::-webkit-scrollbar{height:6px}
        .cn-scroll::-webkit-scrollbar-thumb{background:#fca5a5;border-radius:9px}
        @keyframes cnBlob{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(12px,-8px) scale(1.1)}}
        @keyframes cnSweep{0%{transform:translateX(0) skewX(-20deg)}100%{transform:translateX(700%) skewX(-20deg)}}
        @keyframes cnBolt{0%,100%{transform:scale(1) rotate(0)}45%{transform:scale(1.2) rotate(-8deg)}55%{transform:scale(1.2) rotate(8deg)}}
        @keyframes cnPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
        @keyframes cnRise{0%{transform:translateY(16px);opacity:0}100%{transform:translateY(0);opacity:1}}
        @keyframes cnTicker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
      `}</style>
    </div>
  );
}
